import { copyFile, exists, mkdir, readDir, readTextFile, remove, writeTextFile } from "@tauri-apps/plugin-fs";
import { resolvePath } from "@/services/drives";
import { closeDbConnection, DB_RELATIVE_PATH } from "@/services/storage/sqlite/connection";
import { logger } from "@/services/logger";

const log = logger.scope("backup");

const CONFIG_RELATIVE_PATH = "Config/settings.json";
const BACKUPS_FOLDER = "Backups";
const MAX_BACKUPS = 5;
const AUTO_BACKUP_INTERVAL_MS = 6 * 60 * 60 * 1000;

export interface BackupInfo {
  id: string;
  createdAt: number;
}

function folderNameFor(timestamp: number): string {
  return new Date(timestamp).toISOString().replace(/[:.]/g, "-");
}

async function backupFile(name: string): Promise<string> {
  return `${await resolvePath(BACKUPS_FOLDER)}/${name}`;
}

/**
 * Snapshots the SQLite database (conversations, memory, knowledge metadata
 * and chunk embeddings all live in the one file — see `DATABASE.md`) and
 * the mirrored settings JSON into `Backups/<timestamp>/`, then rotates down
 * to the last `MAX_BACKUPS`. Deliberately doesn't copy document originals or
 * model files — those are large, and are either still reachable at their
 * source path or trivially re-downloadable, so backing them up here would
 * just duplicate storage for no real safety benefit.
 */
export async function createBackup(): Promise<BackupInfo> {
  const dbPath = await resolvePath(DB_RELATIVE_PATH);
  const configPath = await resolvePath(CONFIG_RELATIVE_PATH);
  const backupsRoot = await resolvePath(BACKUPS_FOLDER);

  const createdAt = Date.now();
  const folder = `${backupsRoot}/${folderNameFor(createdAt)}`;
  await mkdir(folder, { recursive: true });

  if (await exists(dbPath)) {
    await copyFile(dbPath, `${folder}/seufic.db`);
  }
  if (await exists(configPath)) {
    await copyFile(configPath, `${folder}/settings.json`);
  }
  await writeTextFile(`${folder}/meta.json`, JSON.stringify({ createdAt }, null, 2));

  await rotateBackups(backupsRoot);
  log.info("Backup created", { folder });
  return { id: folderNameFor(createdAt), createdAt };
}

async function rotateBackups(backupsRoot: string): Promise<void> {
  const entries = await readDir(backupsRoot);
  const folders = entries.filter((e) => e.isDirectory).sort((a, b) => a.name.localeCompare(b.name));
  const excess = folders.length - MAX_BACKUPS;
  for (let i = 0; i < excess; i++) {
    const folder = folders[i];
    if (folder) await remove(`${backupsRoot}/${folder.name}`, { recursive: true });
  }
}

export async function listBackups(): Promise<BackupInfo[]> {
  const backupsRoot = await resolvePath(BACKUPS_FOLDER);
  if (!(await exists(backupsRoot))) return [];

  const entries = await readDir(backupsRoot);
  const infos = await Promise.all(
    entries
      .filter((e) => e.isDirectory)
      .map(async (folder): Promise<BackupInfo> => {
        try {
          const meta = JSON.parse(await readTextFile(`${backupsRoot}/${folder.name}/meta.json`)) as {
            createdAt: number;
          };
          return { id: folder.name, createdAt: meta.createdAt };
        } catch {
          return { id: folder.name, createdAt: 0 };
        }
      }),
  );
  return infos.sort((a, b) => b.createdAt - a.createdAt);
}

/** Restores database + settings from a backup. Closes the live database
 * connection first (overwriting an open file can fail or corrupt state,
 * especially on Windows) — the next read reopens it against the restored
 * file automatically. */
export async function restoreBackup(id: string): Promise<void> {
  const folder = await backupFile(id);
  const dbPath = await resolvePath(DB_RELATIVE_PATH);
  const configPath = await resolvePath(CONFIG_RELATIVE_PATH);

  await closeDbConnection();

  if (await exists(`${folder}/seufic.db`)) {
    await copyFile(`${folder}/seufic.db`, dbPath);
  }
  if (await exists(`${folder}/settings.json`)) {
    await copyFile(`${folder}/settings.json`, configPath);
  }
  log.info("Backup restored", { id });
}

let autoBackupTimer: ReturnType<typeof setInterval> | null = null;

/** Runs an initial backup shortly after startup (if the last one is more
 * than the interval old) and then on a fixed interval for the rest of the
 * process's life. */
export async function scheduleAutoBackup(): Promise<void> {
  if (autoBackupTimer) return;

  try {
    const existing = await listBackups();
    const latest = existing[0];
    if (!latest || Date.now() - latest.createdAt > AUTO_BACKUP_INTERVAL_MS) {
      await createBackup();
    }
  } catch (err) {
    log.warn("Initial automatic backup failed", err);
  }

  autoBackupTimer = setInterval(() => {
    void createBackup().catch((err: unknown) => log.warn("Automatic backup failed", err));
  }, AUTO_BACKUP_INTERVAL_MS);
}
